/**
 * Política de carga del perfil de sesión (`get_current_user_profile`).
 *
 * Hasta 2026-10 cualquier fallo de la RPC dejaba `profile = null` y la app
 * pintaba "No pudimos cargar tu perfil", aunque el perfil ya estuviera cargado
 * y el fallo fuera un parpadeo de red al volver de segundo plano. supabase-js
 * emite SIGNED_IN cada vez que la pestaña vuelve a estar visible
 * (`_recoverAndRefresh`) y lo reenvía a las demás pestañas por BroadcastChannel,
 * así que el perfil se relee muchas veces al día: un solo intento fallido bastaba
 * para tumbar la pantalla hasta el siguiente cambio de pestaña o "Reintentar".
 *
 * Aquí vive la parte pura (clasificar el fallo, backoff, decidir qué hacer) para
 * poder probarla sin el cliente de Supabase ni los `define` de Vite.
 */

/** Cuántos intentos hace una carga antes de rendirse. */
export const MAX_INTENTOS_PERFIL = 4;

/** Tiempo máximo por intento antes de darlo por colgado. */
export const TIMEOUT_INTENTO_PERFIL_MS = 10000;

export const MENSAJE_TIMEOUT_PERFIL = "Profile fetch timeout";

/**
 * - `transitorio`: red, timeout, 5xx, 429, lock de auth. Reintentar tal cual.
 * - `sesion`: el token no sirvió (401, JWT vencido, la petición salió como anon
 *   y no trajo filas). Refrescar la sesión y reintentar.
 * - `definitivo`: la BD respondió con un error que no cambia al reintentar.
 */
export type ClaseFalloPerfil = "transitorio" | "sesion" | "definitivo";

type ErrorParcial = {
  name?: string;
  message?: string;
  code?: string;
  status?: number;
  details?: string;
  hint?: string;
};

/** Códigos de PostgREST que significan "el JWT no sirve" (vencido / inválido). */
const CODIGOS_JWT = new Set(["PGRST301", "PGRST302", "PGRST303"]);

/** Fragmentos de mensaje de los navegadores cuando `fetch` no llega al servidor. */
const MENSAJES_RED = [
  "failed to fetch", // Chrome
  "load failed", // Safari
  "networkerror", // Firefox
  "network request failed",
  "the network connection was lost",
  "fetch failed",
  "aborted",
  "timeout",
];

export function clasificarErrorPerfil(error: unknown): ClaseFalloPerfil {
  if (!error || typeof error !== "object") return "transitorio";
  const err = error as ErrorParcial;
  const mensaje = `${err.message ?? ""} ${err.details ?? ""}`.toLowerCase();
  const nombre = err.name ?? "";
  // postgrest-js devuelve `code: ""` cuando fetch truena: vacío = sin código.
  const code = err.code || undefined;

  // Lock de auth de supabase-js que no se obtuvo a tiempo (otra pestaña o un
  // refresh en curso lo tiene).
  if (/lock/i.test(nombre) || mensaje.includes("navigatorlock") || mensaje.includes("acquiring an exclusive")) {
    return "transitorio";
  }

  if (code && CODIGOS_JWT.has(code)) return "sesion";
  if (mensaje.includes("jwt")) return "sesion";
  if (err.status === 401) return "sesion";

  if (nombre === "AbortError" || nombre === "TypeError" || nombre === "AuthRetryableFetchError") {
    return "transitorio";
  }
  if (err.status === 0 || err.status === 408 || err.status === 429) return "transitorio";
  if (typeof err.status === "number" && err.status >= 500) return "transitorio";
  // PostgREST no alcanzó la BD / pool agotado / statement timeout.
  if (code && (code.startsWith("PGRST0") || code === "57014" || code.startsWith("08"))) {
    return "transitorio";
  }
  if (MENSAJES_RED.some((m) => mensaje.includes(m))) return "transitorio";

  // Sin status ni código reconocible: lo más probable es un fallo del cliente
  // (fetch roto, JSON cortado). Reintentar es barato; rendirse no.
  if (err.status === undefined && code === undefined) return "transitorio";

  return "definitivo";
}

/**
 * Espera antes del intento `intento` (0 = primer reintento). Exponencial con
 * tope: 400 ms, 800 ms, 1.6 s, 3.2 s, ... hasta `maxMs`.
 */
export function backoffPerfilMs(intento: number, baseMs = 400, maxMs = 4000): number {
  const n = Math.max(0, Math.floor(intento));
  return Math.min(maxMs, baseMs * 2 ** n);
}

export type AccionTrasFalloPerfil =
  /** Esperar el backoff (y refrescar sesión si aplica) y volver a intentar. */
  | "reintentar"
  /** Rendirse sin tocar el perfil que ya había: el fallo fue de red. */
  | "conservar"
  /** Rendirse y dejar el perfil en null: pantalla "No pudimos cargar tu perfil". */
  | "error";

export interface FalloPerfil {
  /** `sin_filas` = la RPC respondió bien pero sin filas. */
  clase: ClaseFalloPerfil | "sin_filas";
  /** Intentos ya hechos, contando el que acaba de fallar (1 = el primero). */
  intentosHechos: number;
  /** Si ya había un perfil cargado de ESTA misma sesión. */
  hayPerfilPrevio: boolean;
  maxIntentos?: number;
}

export function decidirTrasFalloPerfil({
  clase,
  intentosHechos,
  hayPerfilPrevio,
  maxIntentos = MAX_INTENTOS_PERFIL,
}: FalloPerfil): AccionTrasFalloPerfil {
  const quedanIntentos = intentosHechos < maxIntentos;

  // Un error que no cambia al reintentar no se repite. Fail-closed: aunque hubiera
  // perfil previo, la BD ya dijo que no.
  if (clase === "definitivo") return "error";

  // Sin filas puede ser "el usuario no existe en public.usuarios" (definitivo)
  // o "la petición salió sin token y auth.uid() fue null" (refrescar y repetir).
  // Se reintenta con refresh; si persiste, es real y se bloquea.
  if (clase === "sin_filas") return quedanIntentos ? "reintentar" : "error";

  if (quedanIntentos) return "reintentar";

  // Agotados los intentos por red/sesión: si ya teníamos el perfil de esta
  // sesión, seguir con él es mejor que expulsar al usuario por un corte de wifi.
  // La desactivación de la cuenta llega por realtime y por el siguiente fetch.
  return hayPerfilPrevio ? "conservar" : "error";
}

/** True si conviene refrescar la sesión antes del siguiente intento. */
export function requiereRefrescarSesion(clase: FalloPerfil["clase"]): boolean {
  return clase === "sesion" || clase === "sin_filas";
}

/**
 * Corre `promesa` con un límite de tiempo. El temporizador se limpia al
 * terminar para no dejar rechazos colgando.
 */
export function conTimeout<T>(promesa: PromiseLike<T>, ms: number, mensaje = MENSAJE_TIMEOUT_PERFIL): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(mensaje)), ms);
    Promise.resolve(promesa).then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}
